import CustomList from "components/list/custom";
import React from "react";
import { useParams } from "react-router-dom";
import BudgetFilters from "@/modules/work/jobs/analytics/budget/filters";
import { useJob } from "@/modules/work/jobs/context";
import { AmountCell } from "@/components/cells/amount";
import { CURRENT_MONTH_END_DATE, CURRENT_MONTH_START_DATE } from "@/lib/utils";
import { HoursCell, TotalHoursCell } from "@/components/hours/cell";

const JobBudget = () => {
  const { jobId } = useParams();

  const { type } = useJob();

  const defaultFilters = {
    jobId: jobId,
  };

  const columns = [
    {
      accessorKey: "department",
      header: "Department",
      cell: ({ getValue }) => <div className="capitalize">{getValue()?.name}</div>,
      footer: () => <div className=""> Total</div>,
    },
    {
      accessorKey: "grade",
      header: "Grade",
      cell: ({ getValue }) => <div className="uppercase">{getValue()?.name}</div>,
      footer: "",
    },
    {
      accessorKey: "allocated_hours",
      header: "Allocated Hours",
      cell: HoursCell,
      footer: TotalHoursCell("allocated_hours"),
    },
    {
      accessorKey: "approved_hours",
      header: "Approved Hours",
      cell: HoursCell,
      footer: TotalHoursCell("approved_hours"),
    },
    {
      accessorKey: "hourly_cost",
      header: "Hourly Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),

      field: "amount",
    },

    {
      accessorKey: "allocated_cost",
      header: "Allocated Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),

      footer: (totals) => (
        <AmountCell
          getValue={() => totals.allocated_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "allocated_share",
      header: "Allocated Share (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.allocated_share}</div>
      ),

      field: "amount",
    },
    {
      accessorKey: "allocated_revenue",
      header: "Allocated Revenue (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.allocated_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "gross_cost",
      header: "Gross Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell getValue={() => totals.gross_cost} className="text-center" />
      ),
      field: "amount",
    },
    {
      accessorKey: "net_cost",
      header: "Net Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell getValue={() => totals.net_cost} className="text-center" />
      ),
      field: "amount",
    },
    {
      accessorKey: "remaining_budget",
      header: "Remaining Budget (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.remaining_budget}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "job_months",
      header: "Job Total Months",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.job_months}</div>
      ),
      field: "amount",
    },
    {
      accessorKey: "expected_revenue",
      header: "Expected Revenue (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.expected_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "job_period_factor",
      header: "Job Period Factor",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.job_period_factor}</div>
      ),
      field: "amount",
    },
    {
      accessorKey: "daily_rate",
      header: "Daily Rate (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      field: "amount",
    },
    {
      accessorKey: "working_days",
      header: "Working Days",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
    },
    {
      accessorKey: "billable_amount",
      header: "Billable Amount (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.billable_amount}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "billable_revenue",
      header: "Billable Revenue (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.billable_revenue}
          className="text-center"
        />
      ),

      field: "amount",
    },
    {
      accessorKey: "billed_revenue",
      header: "Billed Revenue (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.billed_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "accrued_gm",
      header: "Accrued GM (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell getValue={() => totals.accrued_gm} className="text-center" />
      ),
      field: "amount",
    },

    {
      accessorKey: "accrued_nm",
      header: "Accrued NM (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell getValue={() => totals.accrued_nm} className="text-center" />
      ),
      field: "amount",
    },
    {
      accessorKey: "accrued_gm_percentage",
      header: "Accrued GM (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.accrued_gm_percentage}</div>
      ),
      field: "amount",
    },
    {
      accessorKey: "accrued_nm_percentage",
      header: "Accrued NM (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.accrued_nm_percentage}</div>
      ),
      field: "amount",
    },
    {
      accessorKey: "realised_gm",
      header: "Realised GM (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.realised_gm}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "realised_nm",
      header: "Realised NM (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.realised_nm}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "realised_gm_percentage",
      header: "Realised GM (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.realised_gm_percentage}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "realised_nm_percentage",
      header: "Realised NM (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.realised_nm_percentage}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "inventory",
      header: "Inventory (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell getValue={() => totals.inventory} className="text-center" />
      ),
      field: "amount",
    },
    {
      accessorKey: "backlog",
      header: "Backlog (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell getValue={() => totals.backlog} className="text-center" />
      ),
      field: "amount",
    },
    {
      accessorKey: "billability",
      header: "Billability (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.billability}</div>
      ),
      field: "amount",
    },
    {
      accessorKey: "budget_margin",
      header: "Budget Margin (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.budget_margin}</div>
      ),
      // field: "amount",
    },
    {
      accessorKey: "achieved_margin",
      header: "Achieved Margin (%)",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.achieved_margin}</div>
      ),
      // field: "amount",
    },
    {
      accessorKey: "period_approved_hours",
      header: "Period Approved Hours",
      cell: HoursCell,
      footer: TotalHoursCell("period_approved_hours"),
      field: "amount",

    },
    {
      accessorKey: "period_approved_cost",
      header: "Period Approved Cost",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_approved_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_billable_revenue",
      header: "Period Billable Revenue (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_billable_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_billed_revenue",
      header: "Period Billed Revenue (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_billed_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_end_approved_hours",
      header: "Period End Approved Hours",
      cell: HoursCell,
      footer: TotalHoursCell("period_end_approved_hours"),
      field: "amount",

    },
    {
      accessorKey: "period_end_approved_cost",
      header: "Period End Approved Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_end_approved_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_end_remaining_budget",
      header: "Period End Remaining Budget (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_end_remaining_budget}
          className="text-center"
        />
      ),
      field: "amount",
    },

    // hourly & bucket
    {
      accessorKey: "billing_rate",
      header: "Billing Rate (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.billing_rate}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "billable_hours",
      header: "Billable Hours",
      cell: HoursCell,
      footer: TotalHoursCell("billable_hours"),
      field: "number",
    },
    {
      accessorKey: "advanced_billing",
      header: "Advanced Billing",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.advanced_billing}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "unbilled_revenue",
      header: "Unbilled Revenue",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.unbilled_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "unbilled_hours",
      header: "Unbilled Hours",
      cell: HoursCell,
      footer: TotalHoursCell("unbilled_hours"),
      field: "number",
    },
    // hourly & dedicated & bucket & recurring
    {
      accessorKey: "period_net_cost",
      header: "Period Net Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_net_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_gross_cost",
      header: "Period Gross Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_gross_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    // hourly
    {
      accessorKey: "billed_hours",
      header: "Billed Hours",
      cell: HoursCell,
      footer: TotalHoursCell("billed_hours"),
      field: "number",
    },
    {
      accessorKey: "advance_billed_hours",
      header: "Advance Billed Hours",
      cell: HoursCell,
      footer: TotalHoursCell("advance_billed_hours"),
      field: "number",
    },
    {
      accessorKey: "period_billable_hours",
      header: "Period Billable Hours",
      cell: HoursCell,
      footer: TotalHoursCell("period_billable_hours"),
      field: "number",
    },
    {
      accessorKey: "period_billed_hours",
      header: "Period Billed Hours",
      cell: HoursCell,
      footer: TotalHoursCell("period_billed_hours"),
      field: "number",
    },
    // dedicated & recurring
    {
      accessorKey: "active_billable_start_date",
      header: "Active Billable Start Date",
      cell: ({ getValue }) => <div className="text-center">{getValue() || "-"}</div>,
    },
    {
      accessorKey: "active_billable_end_date",
      header: "Active Billable End Date",
      cell: ({ getValue }) => <div className="text-center">{getValue() || "-"}</div>,
    },
    {
      accessorKey: "active_billable_gross_cost",
      header: "Active Billable Gross Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.active_billable_gross_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "active_billable_net_cost",
      header: "Active Billable Net Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.active_billable_net_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "active_billable_working_days",
      header: "Active Billable Working Days",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      field: "number",
    },
    {
      accessorKey: "active_billable_amount",
      header: "Active Billable Amount (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.active_billable_amount}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "active_billable_revenue",
      header: "Active Billable Revenue (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.active_billable_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "active_allocated_cost",
      header: "Active Allocated Cost (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.active_allocated_cost}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "active_remaining_budget",
      header: "Active Remaining Budget (₹)",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.active_remaining_budget}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_working_days",
      header: "Period Working Days",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      field: "number",
    },
    {
      accessorKey: "period_overlap",
      header: "Period Overlap",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      field: "number",
    },
    // bucket
    {
      accessorKey: "consumed_hours",
      header: "Consumed Hours",
      cell: HoursCell,
      footer: TotalHoursCell("consumed_hours"),
      field: "number",
    },
    {
      accessorKey: "remaining_hours",
      header: "Remaining Hours",
      cell: HoursCell,
      footer: TotalHoursCell("remaining_hours"),
      field: "number",
    },
    {
      accessorKey: "advance_hours",
      header: "Advance Hours",
      cell: HoursCell,
      footer: TotalHoursCell("advance_hours"),
      field: "number",
    },
    {
      accessorKey: "accrued_revenue",
      header: type === "bucket" ? "Redeemed Revenue" : "Accrued Revenue",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.accrued_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_accrued_revenue",
      header: "Period Accrued Revenue",
      cell: ({ getValue }) => (
        <AmountCell getValue={getValue} className="text-center" />
      ),
      footer: (totals) => (
        <AmountCell
          getValue={() => totals.period_accrued_revenue}
          className="text-center"
        />
      ),
      field: "amount",
    },
    {
      accessorKey: "period_consumed_hours",
      header: "Period Consumed Hours",
      cell: HoursCell,
      footer: TotalHoursCell("period_consumed_hours"),
      field: "number",
    },
    {
      accessorKey: "period_achieved_margin",
      header: "Period Achieved Margin",
      cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
      footer: (totals) => (
        <div className="text-center">{totals.period_achieved_margin}</div>
      ),
      field: "number",
    },
  ];

  //  Filter columns based on job type
  const getFilteredColumns = (type, columns) => {
    const fixedColumns = [
      "department",
      "grade",
      "allocated_hours",
      "approved_hours",
      "hourly_cost",
      "allocated_cost",
      "allocated_share",
      "allocated_revenue",
      "gross_cost",
      "net_cost",
      "remaining_budget",
      "billable_revenue",
      "billed_revenue",
      "accrued_gm",
      "accrued_nm",
      "accrued_gm_percentage",
      "accrued_nm_percentage",
      "realised_gm_percentage",
      "realised_nm_percentage",
      "realised_gm",
      "realised_nm",
      "inventory",
      "backlog",
      "billability",
      "budget_margin",
      "achieved_margin",
      "period_approved_cost",
      "period_approved_hours",
      "period_end_approved_hours",
      "period_end_approved_cost",
      "period_end_remaining_budget",
      "period_billable_revenue",
      "period_billed_revenue",
      "period_approved_cost",
    ];

    const hourlyColumns = [
      "department",
      "grade",
      "approved_hours",
      "hourly_cost",
      "allocated_cost",
      "allocated_share",
      "allocated_revenue",
      "gross_cost",
      "net_cost",
      "remaining_budget",
      "billable_revenue",
      "billed_revenue",
      "accrued_gm",
      "accrued_nm",
      "accrued_gm_percentage",
      "accrued_nm_percentage",
      "realised_gm_percentage",
      "realised_nm_percentage",
      "realised_gm",
      "realised_nm",
      "inventory",
      "backlog",
      "billability",
      "budget_margin",
      "achieved_margin",
      "billing_rate",
      "billable_hours",
      "billed_hours",
      "advanced_billing",
      "unbilled_revenue",
      "advance_billed_hours",
      "unbilled_hours",
      "period_net_cost",
      "period_billable_revenue",
      "period_billed_revenue",
      "period_billable_hours",
      "period_billed_hours",
      "period_gross_cost",
    ];

    const dedicatedColumns = [
      "department",
      "grade",
      "allocated_hours",
      "approved_hours",
      "hourly_cost",
      "allocated_cost",
      "allocated_share",
      "allocated_revenue",
      "expected_revenue",
      "job_period_factor",
      "job_months",
      "gross_cost",
      "net_cost",
      "remaining_budget",
      "billable_revenue",
      "billed_revenue",
      "accrued_gm",
      "accrued_nm",
      "accrued_gm_percentage",
      "accrued_nm_percentage",
      "realised_gm_percentage",
      "realised_nm_percentage",
      "realised_gm",
      "realised_nm",
      "inventory",
      "backlog",
      "billability",
      "budget_margin",
      "achieved_margin",
      "active_billable_start_date",
      "active_billable_end_date",
      "active_billable_gross_cost",
      "active_billable_net_cost",
      "active_billable_working_days",
      "active_billable_amount",
      "active_allocated_cost",
      "active_remaining_budget",
      "period_net_cost",
      "period_working_days",
      "period_overlap",
      "period_billable_revenue",
      "period_billed_revenue",
      "period_gross_cost",
    ];

    const recurringColumns = [
      "department",
      "grade",
      "approved_hours",
      "hourly_cost",
      "allocated_cost",
      "allocated_share",
      "allocated_revenue",
      "gross_cost",
      "net_cost",
      "remaining_budget",
      "billable_revenue",
      "billed_revenue",
      "accrued_gm",
      "accrued_nm",
      "accrued_gm_percentage",
      "accrued_nm_percentage",
      "realised_gm_percentage",
      "realised_nm_percentage",
      "realised_gm",
      "realised_nm",
      "inventory",
      "backlog",
      "billability",
      "budget_margin",
      "achieved_margin",
      "active_billable_start_date",
      "active_billable_end_date",
      "active_billable_gross_cost",
      "active_billable_net_cost",
      "active_billable_amount",
      "active_billable_revenue",
      "active_allocated_cost",
      "active_remaining_budget",
      "period_net_cost",
      "period_working_days",
      "period_overlap",
      "period_billable_revenue",
      "period_billed_revenue",
      "period_gross_cost",
      "billable_amount",
      "daily_rate",
      "working_days"
    ];

    const bucketColumns = [
      "department",
      "grade",
      "approved_hours",
      "hourly_cost",
      "allocated_cost",
      "allocated_share",
      "allocated_revenue",
      "gross_cost",
      "net_cost",
      "remaining_budget",
      "billable_revenue",
      "billed_revenue",
      "accrued_revenue",
      "accrued_gm",
      "accrued_nm",
      "accrued_gm_percentage",
      "accrued_nm_percentage",
      "realised_gm_percentage",
      "realised_nm_percentage",
      "realised_gm",
      "realised_nm",
      "inventory",
      "backlog",
      "billability",
      "budget_margin",
      "achieved_margin",
      "billing_rate",
      "billable_hours",
      // "consumed_hours",
      "remaining_hours",
      "unbilled_hours",
      "advance_hours",
      "advanced_billing",
      "unbilled_revenue",
      "period_net_cost",
      "period_billable_revenue",
      "period_billed_revenue",
      "period_accrued_revenue",
      // "period_consumed_hours",
      "period_achieved_margin",
      "period_gross_cost"
    ];

    const defaultColumns = [
      "approved_hours",
      "department",
      "grade",
      "hours",
      "hourly_cost",
      "allocated_cost",
      "allocated_share",
      "allocated_revenue",
      "gross_cost",
      "net_cost",
      "remaining_budget",
      "billable_revenue",
      "billed_revenue",
      "accrued_gm",
      "accrued_nm",
      "accrued_gm_percentage",
      "accrued_nm_percentage",
      "realised_gm_percentage",
      "realised_nm_percentage",
      "realised_gm",
      "realised_nm",
      "inventory",
      "backlog",
      "billability",
      "budget_margin",
      "achieved_margin",
    ];

    switch (type) {
      case "fixed":
        return columns.filter(col => fixedColumns.includes(col.accessorKey));
      case "hourly":
        return columns.filter(col => hourlyColumns.includes(col.accessorKey));
      case "dedicated":
        return columns.filter(col => dedicatedColumns.includes(col.accessorKey));
      case "bucket":
        // Order follows bucketColumns, not the master columns array
        return bucketColumns
          .map(key => columns.find(col => col.accessorKey === key))
          .filter(Boolean);
      case "recurring":
        return columns.filter(col => recurringColumns.includes(col.accessorKey));
      default:
        return columns.filter(col => defaultColumns.includes(col.accessorKey));
    }
  }

  return (
    <div className=" flex-1">
      <CustomList
        storageKey={jobId}
        endPoint="revenue-calculation/job"
        permissionEndPoint="billables"
        filters={defaultFilters}
        title="Budget"
        hidePagination={true}
        columns={getFilteredColumns(type, columns)}
        renderFilters={BudgetFilters}
        teleportFilters={true}
      />
    </div>
  );
};


export default JobBudget;